/**
 * The account-specific area: what your own integration is doing. Webhook
 * deliveries are real platform data. Credentials management and the request
 * log have no customer-facing backend yet; the live build says so, the demo
 * build shows the intended structure with sample rows.
 */
import { Link } from 'react-router';
import { GatewayError, formatAgoShort, formatDateTime } from '@p1plug/platform';
import {
  ApiPath,
  Button,
  DataTable,
  Facts,
  NotConnected,
  PageHeader,
  ResourceView,
  Section,
  Segmented,
  State,
  useNow,
  useResource,
} from '@p1plug/ui';
import { useState, type ReactNode } from 'react';
import { tenant } from '../gateway';

type Environment = 'sandbox' | 'production';

/**
 * The account pages need an organisation behind the session. Where the site
 * runs without one (the public deployment, until sign-in exists) they say so
 * instead of showing an error about credentials.
 */
function SignedIn({ title, lead, children }: { title: string; lead: string; children: ReactNode }) {
  const org = useResource(() => tenant.organisation(), []);
  const refused = org.error instanceof GatewayError && (org.error.status === 401 || org.error.status === 403);
  if (!refused) return <>{children}</>;
  return (
    <>
      <PageHeader title={title} lead={lead} />
      <NotConnected title="Sign-in is not available yet">
        <p>
          This page shows your own organisation’s integration. Signing in to the developer site is being built; until
          then your P1Plug contact can give you the same information.
        </p>
        <p>
          The documentation needs no account: start with the <Link to="/docs/quickstart">quickstart</Link>.
        </p>
      </NotConnected>
    </>
  );
}

function EnvironmentSwitch({ value, onChange }: { value: Environment; onChange: (e: Environment) => void }) {
  return (
    <Segmented<Environment>
      label="Environment"
      value={value}
      onChange={onChange}
      options={[
        { value: 'sandbox', label: 'Sandbox' },
        { value: 'production', label: 'Production' },
      ]}
    />
  );
}

export function AccountCredentials() {
  const now = useNow(5000);
  const [environment, setEnvironment] = useState<Environment>('sandbox');
  const org = useResource(() => tenant.organisation(), []);
  const clients = useResource(() => tenant.clients(), []);
  return (
    <SignedIn title="Applications and credentials" lead="An application is one of your services that calls P1Plug.">
      <PageHeader
        title="Applications and credentials"
        lead="An application is one of your services that calls P1Plug. Each has its own machine client, with only the scopes it needs."
        actions={<EnvironmentSwitch value={environment} onChange={setEnvironment} />}
      />
      <Section title="This session">
        <ResourceView resource={org} what="your organisation">
          {(o) => (
            <Facts
              items={[
                { term: 'Organisation', value: o.name },
                { term: 'Organisation ID', value: o.id, mono: true },
                { term: 'Signed in with', value: o.credential },
                { term: 'Scopes', value: o.scopes.join(' '), mono: true },
              ]}
            />
          )}
        </ResourceView>
      </Section>
      <Section
        title="Machine clients"
        aside={
          environment === 'sandbox'
            ? 'Sandbox credentials cannot reach production'
            : 'Production credentials reach customer data'
        }
      >
        <ResourceView resource={clients} what="your machine clients">
          {(c) =>
            c.available ? (
              <>
                <DataTable
                  rowKey={(x) => x.id}
                  columns={[
                    {
                      key: 'name',
                      label: 'Application',
                      render: (x) => (
                        <>
                          {x.name}
                          <span className="p1x-sub-line p1-data">{x.id}</span>
                        </>
                      ),
                    },
                    { key: 'scopes', label: 'Scopes', mono: true, render: (x) => x.scopes.join(' ') },
                    {
                      key: 'status',
                      label: 'Status',
                      render: (x) => <State tone={x.status === 'Enabled' ? 'success' : 'unknown'} label={x.status} />,
                    },
                    {
                      key: 'lastUsed',
                      label: 'Last used',
                      numeric: true,
                      render: (x) => (x.lastUsedAt ? `${formatAgoShort(x.lastUsedAt, now)} ago` : 'Never'),
                    },
                    { key: 'created', label: 'Created', render: (x) => formatDateTime(x.createdAt, 'en') },
                  ]}
                  rows={c.value.filter((x) => x.environment === environment)}
                />
                <div className="dev-account-actions">
                  <Button
                    label="Create machine client"
                    disabled
                    reason="Demo data: creating a client shows its secret once, which needs a real sign-in."
                  />
                </div>
              </>
            ) : (
              <NotConnected title="Self-service credentials are not available yet">
                <p>{c.reason}</p>
                <p>
                  What will be here: create a machine client per service, choose its scopes, see when it was last used,
                  rotate its secret (shown once) and revoke it. Sandbox and production clients are separate.
                </p>
                <p>
                  How it works meanwhile: <Link to="/docs/authentication">Authentication</Link>.
                </p>
              </NotConnected>
            )
          }
        </ResourceView>
      </Section>
    </SignedIn>
  );
}

export function AccountWebhooks() {
  const now = useNow(5000);
  const webhook = useResource(() => tenant.webhook(), [], { refreshMs: 20_000 });
  const deliveries = useResource(() => tenant.deliveries(), [], { refreshMs: 20_000 });
  return (
    <SignedIn title="Webhook deliveries" lead="Every request P1Plug sent to your endpoint.">
      <PageHeader
        title="Webhook deliveries"
        lead="Every request P1Plug sent to your endpoint: what you answered, how long it took, and which attempt it was."
      />
      <Section title="Endpoint">
        <ResourceView resource={webhook} what="your webhook endpoint">
          {(w) =>
            w.configured ? (
              <Facts
                columns={2}
                items={[
                  { term: 'URL', value: w.url, mono: true },
                  {
                    term: 'State',
                    value: (
                      <State
                        tone={w.stalled ? 'critical' : w.healthy ? 'success' : 'warning'}
                        label={
                          w.stalled ? 'Paused after repeated failures' : w.healthy ? 'Delivering' : 'Failing, retrying'
                        }
                      />
                    ),
                  },
                  { term: 'Events waiting', value: w.pending },
                  { term: 'Events delivered', value: w.delivered.toLocaleString('en-GB') },
                  { term: 'Last error', value: w.lastError, mono: true },
                  {
                    term: 'Last delivered',
                    value: w.lastSuccessAt ? `${formatAgoShort(w.lastSuccessAt, now)} ago` : 'Never',
                  },
                ]}
              />
            ) : (
              <p className="p1x-muted">
                No endpoint registered. Register one with <ApiPath method="PUT" path="/api/v1/webhooks/endpoint" />; see{' '}
                <Link to="/docs/webhooks">Webhooks and events</Link>.
              </p>
            )
          }
        </ResourceView>
      </Section>
      <Section title="Recent deliveries">
        <ResourceView resource={deliveries} what="recent deliveries">
          {(list) =>
            list.length === 0 ? (
              <p className="p1x-muted">
                No deliveries yet. The first is sent when something happens to one of your orders or devices.
              </p>
            ) : (
              <DataTable
                rowKey={(d) => d.id}
                columns={[
                  { key: 'id', label: 'Delivery ID', mono: true },
                  { key: 'at', label: 'Time', render: (d) => formatDateTime(d.at, 'en') },
                  {
                    key: 'ok',
                    label: 'Result',
                    render: (d) => <State tone={d.ok ? 'success' : 'critical'} label={d.ok ? 'Delivered' : 'Failed'} />,
                  },
                  { key: 'http', label: 'Your response', numeric: true, render: (d) => d.httpStatus ?? '–' },
                  { key: 'attempt', label: 'Attempt', numeric: true },
                  { key: 'events', label: 'Events', numeric: true },
                  {
                    key: 'duration',
                    label: 'Latency',
                    numeric: true,
                    render: (d) => (d.durationMs == null ? '–' : `${d.durationMs} ms`),
                  },
                  { key: 'error', label: 'Error', render: (d) => d.error ?? '' },
                ]}
                rows={list}
              />
            )
          }
        </ResourceView>
      </Section>
    </SignedIn>
  );
}

export function AccountUsage() {
  const requests = useResource(() => tenant.requestLog(), []);
  return (
    <SignedIn title="API usage" lead="Your latest requests to P1Plug.">
      <PageHeader
        title="API usage"
        lead="Your latest requests to P1Plug: what you sent, what came back and how long it took."
      />
      <ResourceView resource={requests} what="your request log">
        {(r) =>
          r.available ? (
            <DataTable
              caption="Latest requests"
              rowKey={(x) => x.id}
              columns={[
                { key: 'request', label: 'Request', render: (x) => <ApiPath method={x.method} path={x.path} /> },
                {
                  key: 'status',
                  label: 'Status',
                  numeric: true,
                  render: (x) => (
                    <State
                      tone={x.status < 400 ? 'success' : x.status < 500 ? 'warning' : 'critical'}
                      label={String(x.status)}
                    />
                  ),
                },
                { key: 'latency', label: 'Latency', numeric: true, render: (x) => `${x.latencyMs} ms` },
                {
                  key: 'error',
                  label: 'Error code',
                  mono: true,
                  render: (x) => (x.errorCode ? <Link to={`/docs/errors#${x.errorCode}`}>{x.errorCode}</Link> : ''),
                },
                { key: 'id', label: 'Request ID', mono: true },
                { key: 'at', label: 'Time', render: (x) => formatDateTime(x.at, 'en') },
              ]}
              rows={r.value}
            />
          ) : (
            <NotConnected title="The request log is not available yet">
              <p>{r.reason}</p>
              <p>
                What will be here: each request with its method and path, status, latency, error code and a request ID
                you can quote to support, per environment.
              </p>
              <p>
                Available now: <Link to="/account/webhooks">webhook deliveries</Link>.
              </p>
            </NotConnected>
          )
        }
      </ResourceView>
    </SignedIn>
  );
}
